"use client";
import { updateDialectAction } from "@/actions/user.actions";
import { ArrowDownIcon, ChevronDownSmallIcon } from "@/components/Icons";
import { Row } from "@/components/Stack";
import {
  CookiesEnum,
  cookiesValues,
  EventNamesEnum,
  LangEnum,
} from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialect } from "@/hooks/useDialect";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { useUserContext } from "@/context/UserProvider";
import {
  cn,
  getClientSideCookieValue,
  getDialectLabelFromDialectObj,
  isTvEnv,
} from "@/utils/common.helpers";
import { useEffect, useRef, useState } from "react";
import { DialectSelectorPopup } from "./DialectSelectorPopup";
import { UserCulturePopup } from "./userCulture/UserCulturePopup";

export const DialectSelector = ({
  dialect,
  showUserCulturePopup = false,
}: {
  dialect: string;
  showUserCulturePopup?: boolean;
}) => {
  const [isDialectSelectorOpen, setIsDialectSelectorOpen] = useState(false);
  const [isUserCulturePopupOpen, setIsUserCulturePopupOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentDialect, availableDialects } = useDialect();
  const { currentLanguage } = useClientTranslations();
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const { updateUserDialect } = useUserContext();
  const hasSelectedUserCulture = !!getClientSideCookieValue(
    cookiesValues[CookiesEnum.UserCulture],
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDesktopDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDesktopDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDesktopDropdownOpen]);

  const handleDialectSelectorClick = () => {
    if (showUserCulturePopup && !hasSelectedUserCulture) {
      setIsUserCulturePopupOpen(true);
      return;
    }

    // Desktop: use inline glass dropdown
    if (window.innerWidth >= 768 && !isTvEnv()) {
      setIsDesktopDropdownOpen((prev) => !prev);
      return;
    }
    // Mobile: use existing modal/popup
    if (showUserCulturePopup) {
      setIsUserCulturePopupOpen(true);
    } else {
      setIsDialectSelectorOpen(true);
    }
  };

  const handleDesktopDialectSelect = async (dialectUrlValue: string) => {
    setIsDesktopDropdownOpen(false);
    if (dialect === dialectUrlValue) return;

    updateUserDialect(dialectUrlValue);
    trackEvent({
      eventName: EventNamesEnum.CONTENT_CULTURE_UPDATED,
      properties: {
        previous_content_culture: dialect,
        updated_content_culture: dialectUrlValue,
        updated_from: "home_page",
      },
    });
    await updateDialectAction({ dialect: dialectUrlValue });
  };

  return (
    <>
      {/* Mobile: existing modal */}
      <DialectSelectorPopup
        isOpen={isDialectSelectorOpen}
        setIsOpen={setIsDialectSelectorOpen}
        dialect={dialect}
      />
      {isUserCulturePopupOpen && <UserCulturePopup tempCulture={dialect} />}

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleDialectSelectorClick}
          className={cn(
            "h-full rounded-lg border border-stg-grey-4 bg-white bg-opacity-10 px-2 py-1 md:border-0 md:bg-transparent md:px-2 md:py-1.5 md:hover:bg-white/10",
            isTvEnv() && "ml-4",
          )}
          data-testid="dialect-selector-button"
        >
          <Row justify="center" align="center" fullHeight={true} gap={0}>
            <span className="text-sm font-medium md:text-base">
              {dialect
                ? getDialectLabelFromDialectObj({
                    dialect: currentDialect,
                    language: currentLanguage as LangEnum,
                  })
                : t("commonHome.select_culture")}
            </span>
            <span className="hidden text-white md:inline">
              <ChevronDownSmallIcon
                className={cn(
                  "h-5 w-5 text-current transition-transform duration-200",
                  isDesktopDropdownOpen && "rotate-180",
                )}
              />
            </span>
            <span className="md:hidden">
              <ArrowDownIcon color="var(--grey-5)" rotate={-90} />
            </span>
          </Row>
        </button>

        {/* Desktop: glass dropdown */}
        {isDesktopDropdownOpen && (
          <div className="absolute left-0 top-full z-[2] hidden rounded-lg border border-stg-grey-3 bg-stg-categories-dropdown-background md:block">
            <div className="max-h-[33vh] min-w-[12rem] overflow-y-auto p-3">
              {availableDialects.map((d) => {
                const label = getDialectLabelFromDialectObj({
                  dialect: d,
                  language: currentLanguage as LangEnum,
                });
                const isSelected = d.urlValue === currentDialect.urlValue;
                return (
                  <button
                    key={d._id}
                    onClick={() => handleDesktopDialectSelect(d.urlValue)}
                    className={cn(
                      "flex w-full items-center gap-3 whitespace-nowrap rounded px-4 py-3 text-left text-base font-medium transition-colors hover:bg-white/10 hover:text-white",
                      isSelected ? "text-white" : "text-stg-grey-6",
                    )}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                      <span
                        className={cn(
                          "h-2.5 w-2.5 rounded-full bg-current",
                          !isSelected && "opacity-0",
                        )}
                      />
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
